// Objets mobiles : un objet posé dont l'élément est « mobile » suit un chemin tracé dans l'éditeur de cartes (un
// poisson qui fait des allers-retours dans l'eau…). Sa position ne dépend que de l'heure : tous les joueurs le voient
// au même endroit, sans rien échanger avec le serveur. C'est un décor : il ne bloque pas le passage.
import { DIRECTION_VECTORS, type Direction, type Position } from './protocol.ts';
import { isKeyOf } from './guards.ts';

/**
 * Comment l'image suit les changements de sens : pas du tout, en miroir (demi-tour), ou en pivotant — et en miroir
 * au demi-tour, pour ne jamais se retrouver la tête en bas.
 */
export type MotionTurn = 'none' | 'mirror' | 'rotate';
export type MotionMode = 'loop' | 'pingpong';

export interface ObjectMotion {
  /** Étapes rejointes tour à tour depuis la case de pose (coin supérieur gauche de l'objet), en ligne droite. */
  points: Position[];
  /** `loop` : retour au départ puis on recommence ; `pingpong` : le chemin à l'envers. */
  mode: MotionMode;
  /** En cases par seconde. */
  speed: number;
  /** Arrêt à chaque étape (et au départ), en millisecondes. */
  pauseMs: number;
  /** Sens dans lequel regarde l'image, telle qu'elle est dans le tileset. */
  facing: Direction;
  turn: MotionTurn;
}

export const MOTION_MODES: Readonly<Record<MotionMode, string>> = { pingpong: 'Aller-retour', loop: 'En boucle (retour au départ)' };
export const MOTION_TURNS: Readonly<Record<MotionTurn, string>> = {
  mirror: 'Miroir (se retourne au demi-tour)', rotate: 'Pivote dans le sens du déplacement (miroir au demi-tour)', none: 'Garde son sens',
};
export const MOTION_LIMITS = { maxPoints: 20, minSpeed: 0.1, maxSpeed: 10, maxPauseMs: 30_000 } as const;
export const DEFAULT_MOTION: Readonly<ObjectMotion> = { points: [], mode: 'pingpong', speed: 1, pauseMs: 0, facing: 'right', turn: 'mirror' };

export const isMotionMode = isKeyOf<MotionMode>(MOTION_MODES);
export const isMotionTurn = isKeyOf<MotionTurn>(MOTION_TURNS);

/** Position (en cases, fractionnaire) et orientation de l'image à un instant donné. */
export interface ObjectPose {
  x: number;
  y: number;
  /** Rotation de l'image autour de son centre, en radians (sens des aiguilles d'une montre). */
  angle: number;
  flipX: boolean;
  flipY: boolean;
  /** Sens du trajet en cours (gardé pendant les pauses) ; immobile : celui où regarde l'image. */
  dir: Direction;
}

interface Leg {
  from: Position;
  to: Position;
  duration: number;
  orientation: Pick<ObjectPose, 'angle' | 'flipX' | 'flipY' | 'dir'>;
}

const STILL = { angle: 0, flipX: false, flipY: false };

/** Sens principal d'un trajet (le plus long des deux axes ; à égalité, l'horizontal). */
const travelDir = (dx: number, dy: number, fallback: Direction): Direction =>
  !dx && !dy ? fallback : Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
/** Angle ramené dans ]-π, π]. */
const normalize = (angle: number) => angle - 2 * Math.PI * Math.ceil((angle - Math.PI) / (2 * Math.PI));

/** Trajets successifs d'un tour complet, avec l'orientation de l'image sur chacun. */
function legsOf(start: Position, motion: ObjectMotion): Leg[] {
  const stops = [start, ...motion.points];
  const route = motion.mode === 'loop' ? [...stops, start] : [...stops, ...stops.slice(0, -1).reverse()];
  const pairs = route.slice(1).map((to, i) => ({ from: route[i]!, to }));
  const [fx, fy] = DIRECTION_VECTORS[motion.facing];
  // Miroir : le long de l'axe où regarde l'image ; un trajet perpendiculaire garde le sens du trajet d'avant. En
  // pivotant, l'image retournée (qui regarde alors à l'opposé) tourne ensuite jusqu'au sens du trajet.
  const along = ({ from, to }: { from: Position; to: Position }) => Math.sign(fx !== 0 ? to.x - from.x : to.y - from.y);
  let flipped = [...pairs].reverse().map(along).find((sign) => sign !== 0) === -(fx || fy);
  let angle = 0;
  let dir = motion.facing;
  return pairs.map(({ from, to }) => {
    const dx = to.x - from.x, dy = to.y - from.y;
    const sign = along({ from, to });
    if (sign !== 0) flipped = sign === -(fx || fy);
    const mirrored = { flipX: fx !== 0 && flipped, flipY: fy !== 0 && flipped };
    if (dx || dy) angle = normalize(Math.atan2(dy, dx) - (flipped ? Math.atan2(-fy, -fx) : Math.atan2(fy, fx)));
    dir = travelDir(dx, dy, dir);
    const orientation = motion.turn === 'rotate' ? { angle, ...mirrored, dir }
      : motion.turn === 'mirror' ? { angle: 0, ...mirrored, dir }
        : { ...STILL, dir };
    return { from, to, duration: (Math.hypot(dx, dy) / motion.speed) * 1000, orientation };
  });
}

/** Où en est l'objet posé en `start` qui suit `motion`, à l'instant `time` (en millisecondes). */
export function objectPose(start: Position, motion: ObjectMotion, time: number): ObjectPose {
  const legs = legsOf(start, motion);
  const cycle = legs.reduce((sum, leg) => sum + leg.duration + motion.pauseMs, 0);
  if (!legs.length || cycle <= 0) return { ...start, ...STILL, dir: motion.facing };
  let t = ((time % cycle) + cycle) % cycle;
  for (const leg of legs) {
    if (t < leg.duration) {
      const progress = t / leg.duration;
      return { x: leg.from.x + (leg.to.x - leg.from.x) * progress, y: leg.from.y + (leg.to.y - leg.from.y) * progress, ...leg.orientation };
    }
    t -= leg.duration;
    if (t < motion.pauseMs) return { ...leg.to, ...leg.orientation };
    t -= motion.pauseMs;
  }
  const last = legs.at(-1)!;
  return { ...last.to, ...last.orientation };
}
